import React, { FC, useCallback, useEffect, useRef } from 'react';

import ReactSelect, { ActionMeta, components, MultiValue, OptionProps, SingleValueProps } from 'react-select';

import classNames from 'classnames';
import MenuItem from '@material-ui/core/MenuItem';
import style from './style.module.css';
import { IFloorplanTimeSelector, ListItem } from './types';
import { getReactSelectStyles } from './style-helper';
import typ from 'design-v2/globals/typography.module.css';
import PreventDoubleClickToZoom from 'design-v2/common/PreventDoubleClickToZoom/index';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { APP_COLORS, Colors } from 'design-v2/globals/colors/colors';
import { cloneDeep, sortBy } from 'lodash-es';
import FloorplanBookingFilterService from 'floorplan-v2/app/services/floorplanBookingFilter';
import { ISimpleSelectTimeDropdown } from 'shared-types/SharedTypes';

const NS = 'FloorplanTimeSelector';

const FloorplanTimeSelector: FC<IFloorplanTimeSelector> = ({
    label,
    list,
    handleSelectItem,
    selectedItemIndex,
    focusValue,
    isDisable,
    isSmallScreen,
  }) => {
  if (!list || !list.length) {
    return null;
  }

  const useStyles = makeStyles({
    singleSelectedValueText: {
      color: '#fff !important',
    },
  });

  const classes = useStyles();

  const selectInputRef = useRef();

  const selectSingleTable = (itemIndex: number) => {
    if (itemIndex >= list.length || itemIndex < 0) {
      console.warn(
        NS,
        `selectSingleTable error on ${label} - index out of range ${itemIndex}/${list.length}`,
        list,
      );
      return;
    }

    const item = list[itemIndex];
    (selectInputRef.current as OptionProps<ListItem>).setValue([item], 'select-option');
  };
  const getReadOnlyValue = (): Readonly<ListItem[]> => {
    return (selectInputRef.current as OptionProps<ListItem>).getValue();
  };

  useEffect(() => {
    if (list.length && selectedItemIndex > -1) {
      const currentValue = getReadOnlyValue();
      if (currentValue?.length && currentValue[0].value === list[selectedItemIndex]?.value) {
        // no use setting if same value
        return;
      }
      selectSingleTable(selectedItemIndex);
    }
  }, [selectedItemIndex, list]);

  const Option = (props: OptionProps<ListItem>) => {
    // OptionProps<ITableDividerHeader>

    /* UI-1351: we got an issue where we wanted to show now time in the proper position in the dropdown
      but when we change the index of the now time it started creating lots of issues in the FP header
      cause when we change the index other functions got executed which was not required.
      So instead we came up with the strategy that we change the order of the divs using css. Flex order
      https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_flexible_box_layout/Ordering_flex_items
      this would keep the original logic but just change the Now time to the proper position
     */
    let order = props.data.indexVal
    const isNow = (props.data.name as string).includes('Now');

    if (isNow) {
      const list: {value: Date}[] = cloneDeep(props.options) as any;
      const sortedTimes = sortBy(list, 'value');
      order = FloorplanBookingFilterService.getTimeNowIndex(sortedTimes as ISimpleSelectTimeDropdown[]);
    }

    return (
      <div id={`${props.data.value}`} ref={props.innerRef} style={{ order: order }}>
        <components.Option {...props}>
          <PreventDoubleClickToZoom>
            <MenuItem
              disableRipple
              className={classNames({
                [typ.typMed]: true,
                [style.menuItemSelectedFp]: props.isSelected,
                [style.largeItemTextSize]: true,
              })}
              value={props.data.value as any}
              style={props.data.style}
            >
              {props.data.name}
            </MenuItem>
          </PreventDoubleClickToZoom>
        </components.Option>
      </div>
    );
  };

  const SingleValue = ({ children, ...props }: SingleValueProps<ListItem>) => {
    let isNow = false;
    let singleText = props.data.selectedLabel ? props.data.selectedLabel : props.data.name;
    if (props.selectProps.placeholder === 'Times') {
      isNow = (props.data.name as string).includes('Now');
      singleText = moment(props.data.value).format('LT');
    }
    return (
      <components.SingleValue {...props}>
        <div
          className={classNames({
            [style.singleSelectedValue]: true,
            [classes.singleSelectedValueText]: true,
            [style.largeItemTextSize]: true,
          })}>
          {isNow && <>
            {isSmallScreen ? <span style={{ padding: '0 5px' }}><FiberManualRecordIcon
                style={{ color: Colors.get(APP_COLORS.clrGood), fontSize: '1.2rem' }} /></span> :
              <span style={{ color: Colors.get(APP_COLORS.clrGood), fontWeight: 'bold' }}>NOW </span>}
          </>}
          {singleText}
        </div>
      </components.SingleValue>
    );
  };

  const getList = useCallback(() => {
    // deleting the duplicate time - Now 12.45 and 12.45
    return FloorplanBookingFilterService.removeDuplicateTimes(list, 'value')
  }, [list]);


  return (
    <div data-testid="my-select-component" className={classNames({
      [style.childContainerSelect]: true,
    })}>
      <ReactSelect
        ref={selectInputRef}
        isDisabled={isDisable}
        options={getList()}
        placeholder={label}
        isSearchable={false}
        isClearable={false}
        hideSelectedOptions={false}
        closeMenuOnSelect={true}
        menuShouldScrollIntoView
        onMenuOpen={() => {
          if (selectedItemIndex === -1) return;

          setTimeout(() => {
            document.getElementById(`${focusValue}`)?.scrollIntoView();
          });
        }}
        // menuIsOpen={true} // useful for debugging

        // getOptionValue={(option: ListItem) => option.value as string}

        // changing 'value' prop causes the whole list to rerender, which leads to terrible performance
        // value={list[localSelectedIndex]}

        menuPortalTarget={document.body}
        components={{
          Option,
          SingleValue,
          DropdownIndicator: ()=> null,
          IndicatorSeparator: ()=> null,
          IndicatorsContainer: ()=> null,
        }}
        onChange={(newValue: ListItem | MultiValue<ListItem>, actionMeta: ActionMeta<ListItem>) => {
          const _selectedIndex: number = list.findIndex((item) => {
            const _newValue: ListItem = Array.isArray(newValue)
              ? (newValue as MultiValue<ListItem>)[0]
              : (newValue as ListItem);
            return item.value === _newValue.value;
          });
          handleSelectItem(_selectedIndex);
        }}
        styles={getReactSelectStyles()}
      />
    </div>

  );
};

export default FloorplanTimeSelector;
